import {
    BehaviorSubject,
    Observable,
    combineLatest,
    filter,
    map,
    take,
} from "rxjs";

import ReviewsSortingOptionsEntity from "../entities/ReviewsSortingOptionsEntity";
import ReviewsStorefrontValuesSettingsEntity from "../entities/ReviewsStorefrontValuesSettingsEntity";

type SortItem = {
    value: string;
    labelTranslationKey: string;
    labelTranslationNamespace: string;
};

export class SortingManager {
    private currentSortValue$ =
        new BehaviorSubject<ReviewsSortingOptionsEntity>("mostRecent");
    private currentSortItem$: Observable<SortItem>;
    private sortItems$: Observable<Array<SortItem>>;

    public constructor(
        private readonly reviewsSettings$: Observable<ReviewsStorefrontValuesSettingsEntity>
    ) {
        this.sortItems$ = this.reviewsSettings$.pipe(
            map((settings) => {
                const sortItems: SortItem[] = [
                    ...(settings?.isReviewsPinningEnabled
                        ? [
                              {
                                  value: "mostRelevant",
                                  labelTranslationKey: "sort_most_relevant",
                                  labelTranslationNamespace:
                                      "reviews.ReviewsProductPageWidget",
                              },
                          ]
                        : []),
                    {
                        value: "mostRecent",
                        labelTranslationKey: "sort_most_recent",
                        labelTranslationNamespace:
                            "reviews.ReviewsProductPageWidget",
                    },
                    {
                        value: "highestRating",
                        labelTranslationKey: "sort_highest_rating",
                        labelTranslationNamespace:
                            "reviews.ReviewsProductPageWidget",
                    },
                    {
                        value: "lowestRating",
                        labelTranslationKey: "sort_lowest_rating",
                        labelTranslationNamespace:
                            "reviews.ReviewsProductPageWidget",
                    },
                    {
                        value: "withPhotos",
                        labelTranslationKey: "sort_with_photos",
                        labelTranslationNamespace:
                            "reviews.ReviewsProductPageWidget",
                    },
                    {
                        value: "mostHelpful",
                        labelTranslationKey: "sort_most_helpful",
                        labelTranslationNamespace:
                            "reviews.ReviewsProductPageWidget",
                    },
                ];

                return sortItems;
            })
        );

        this.reviewsSettings$.pipe(take(1)).subscribe((settings) => {
            if (
                settings.defaultSortingOption &&
                settings.defaultSortingOption !== "mostRecent"
            ) {
                this.currentSortValue$.next(settings.defaultSortingOption);
            }
        });

        this.currentSortItem$ = combineLatest([
            this.sortItems$,
            this.currentSortValue$,
        ]).pipe(
            filter(([sortItems, currentSortValue]) =>
                sortItems.some((item) => item.value === currentSortValue)
            ),
            map(([sortItems, currentSortValue]) => {
                const item = sortItems.find(
                    (sortItem) => sortItem.value === currentSortValue
                );

                if (!item) {
                    throw new Error(`Invalid sort value: ${currentSortValue}`);
                }

                return item;
            })
        );
    }

    public selectCurrentSortValue(): Observable<ReviewsSortingOptionsEntity> {
        return this.currentSortValue$.asObservable();
    }

    public selectCurrentSortItem(): Observable<SortItem> {
        return this.currentSortItem$;
    }

    public selectSortItems(): Observable<Array<SortItem>> {
        return this.sortItems$;
    }

    public setCurrentSort(sortValue: string): void {
        this.currentSortValue$.next(sortValue as ReviewsSortingOptionsEntity);
    }
}
